import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Copy,
  Loader2,
  MapPin,
  MessageCircle,
  XCircle,
} from "lucide-react";
import { SHUBHAM_BRAND } from "./shubhamBrandAssets";

const SHUBHAM_WHATSAPP_NUMBER = "917575801703";

const statusConfig = {
  auth: {
    icon: <CheckCircle className="h-5 w-5 text-emerald-600" />,
    title: "Original Product",
    boxClassName: "border-emerald-200 bg-emerald-50",
    textClassName: "text-emerald-800",
  },
  unauth: {
    icon: <XCircle className="h-5 w-5 text-red-600" />,
    title: "Unauthentic Product",
    boxClassName: "border-red-200 bg-red-50",
    textClassName: "text-red-700",
  },
  duplicate: {
    icon: <Copy className="h-5 w-5 text-amber-600" />,
    title: "Duplicate Scan Detected",
    boxClassName: "border-amber-200 bg-amber-50",
    textClassName: "text-amber-700",
  },
};

const messageConfig = {
  unauth: {
    icon: <XCircle className="h-5 w-5 text-red-600" />,
    boxClassName: "border-red-200 bg-red-50",
    textClassName: "text-red-700",
  },
  duplicate: {
    icon: <Copy className="h-5 w-5 text-amber-600" />,
    boxClassName: "border-amber-200 bg-amber-50",
    textClassName: "text-amber-700",
  },
  default: {
    icon: <AlertTriangle className="h-5 w-5 text-slate-500" />,
    boxClassName: "border-slate-200 bg-slate-50",
    textClassName: "text-slate-700",
  },
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  return `${value}/-`;
};

const formatValue = (value) => {
  if (Array.isArray(value)) {
    const filteredValue = value.filter(Boolean);
    return filteredValue.length ? filteredValue : ["N/A"];
  }

  return value || "N/A";
};

const DetailsCard = ({ title, rows }) => (
  <div className="overflow-hidden border-2 border-gray-300 bg-white shadow-md">
    <div className="border-b-2 border-gray-300 bg-[#295a80] px-4 py-3">
      <h2 className="text-center text-lg font-semibold text-white md:text-xl">{title}</h2>
    </div>

    <div className="flex flex-col">
      {rows.map(([label, value], index) => {
        const displayValue = formatValue(value);

        return (
          <div
            key={`${title}-${label}`}
            className={`flex border-b border-gray-300 last:border-b-0 ${
              index % 2 === 0 ? "bg-gray-50" : "bg-white"
            }`}
          >
            <div className="flex w-[42%] items-center border-r border-gray-300 px-3 py-3 text-sm font-bold text-[#295a80] sm:px-5 sm:text-[14px] lg:px-6 lg:text-[15px]">
              {label}
            </div>
            <div className="flex w-[58%] items-center break-words px-4 py-3 text-sm font-medium text-gray-800 sm:px-5 sm:text-[15px] lg:px-6 lg:text-[16px]">
              {Array.isArray(displayValue) ? (
                <div className="space-y-1">
                  {displayValue.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              ) : (
                displayValue
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const StatusBanner = ({ status }) => {
  if (!statusConfig[status]) {
    return null;
  }

  const currentStatus = statusConfig[status];

  return (
    <div className={`mb-6 rounded-lg border p-4 ${currentStatus.boxClassName}`}>
      <div className="flex items-center justify-center gap-3 text-center">
        <div className="rounded-full bg-white p-2 shadow-sm">{currentStatus.icon}</div>
        <h3 className={`text-base font-semibold ${currentStatus.textClassName}`}>
          {currentStatus.title}
        </h3>
      </div>
    </div>
  );
};

const MessageBanner = ({ status, message }) => {
  if (!message) {
    return null;
  }

  const currentMessage = messageConfig[status] || messageConfig.default;

  return (
    <div className={`mb-6 flex items-start gap-3 rounded-lg border p-4 ${currentMessage.boxClassName}`}>
      <div className="mt-0.5">{currentMessage.icon}</div>
      <p className={`text-sm font-medium ${currentMessage.textClassName}`}>{message}</p>
    </div>
  );
};

const buildWhatsappMessage = ({ productData, status, message }) => {
  const lines = [
    `Hello ${SHUBHAM_BRAND.companyName} team,`,
    "",
    "I would like to share feedback about the product:",
    `Product Name: ${formatValue(productData?.productName)}`,
    `Product Code: ${formatValue(productData?.productCode)}`,
    `Batch No.: ${formatValue(productData?.batchNumber)}`,
    `Unit Code: ${formatValue(productData?.batchName)}`,
    `Mfg Date: ${formatValue(productData?.manufacturingDate)}`,
    `Exp Date: ${formatValue(productData?.expiryDate)}`,
    `MRP: ${formatCurrency(productData?.mrp)}`,
  ];

  // if (status) {
  //   lines.push(`Verification Status: ${status}`);
  // }

  // if (message) {
  //   lines.push(`Verification Message: ${message}`);
  // }

  lines.push("", "My feedback:");

  return lines.join("\n");
};

const ShubhamProductDetailsView = ({
  productData,
  locationData,
  status,
  loading,
  message,
  onBack,
}) => {
  const showDetails = status === "auth" && productData && !loading;
  const canSendFeedback = productData && !loading;

  const handleSendFeedback = () => {
    const whatsappMessage = buildWhatsappMessage({ productData, status, message });
    const whatsappUrl = `https://wa.me/${SHUBHAM_WHATSAPP_NUMBER}?text=${encodeURIComponent(
      whatsappMessage
    )}`;

    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="flex items-center justify-between gap-3 border-b-2 border-gray-200 bg-white p-4 shadow-sm md:gap-4 md:p-6">
        <div className="w-10">
          {onBack ? (
            <button
              onClick={onBack}
              className="rounded-full border border-gray-200 bg-white p-2 shadow-sm transition-shadow hover:shadow-md"
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5 text-[#295a80]" />
            </button>
          ) : null}
        </div>

        <div className="flex items-center justify-center">
          <img
            src={SHUBHAM_BRAND.logo}
            alt="Shubham Logo"
            className="h-14 w-auto object-contain md:h-16"
          />
        </div>

        <div className="w-10" />
      </div>

      <div className="mx-auto max-w-5xl px-4 py-6">
        <StatusBanner status={status} />
        {/* <MessageBanner status={status} message={message} /> */}

        {loading && (
          <div className="flex min-h-[280px] flex-col items-center justify-center bg-white shadow-md">
            <Loader2 className="h-10 w-10 animate-spin text-[#295a80]" />
            <p className="mt-4 text-gray-600">Verifying product...</p>
          </div>
        )}

        {showDetails && (
          <div className="mx-auto max-w-3xl space-y-6">
            <DetailsCard
              title="Product Details"
              rows={[
                ["Product Name", productData.productName],
                ["Product Code", productData.productCode],
                ["Batch No.", productData.batchNumber],
                ["Unit Code", productData.batchName],
                ["Mfg Date", productData.manufacturingDate],
                ["Exp Date", productData.expiryDate],
                ["MRP", formatCurrency(productData.mrp)],
              ]}
            />

            {canSendFeedback && (
              <div className="flex justify-center">
                <button
                  onClick={handleSendFeedback}
                  className="inline-flex items-center gap-2 rounded-lg border border-[#295a80] bg-[#295a80] px-5 py-3 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#234d6d] hover:shadow-lg"
                >
                  <MessageCircle className="h-5 w-5" />
                  Send Feedback
                </button>
              </div>
            )}
          </div>
        )}

        {!loading && !showDetails && status === null && (
          <div className="flex min-h-[220px] items-center justify-center rounded-lg bg-white p-8 text-center shadow-md">
            <div>
              <MapPin className="mx-auto h-8 w-8 text-[#295a80]" />
              <p className="mt-3 text-gray-600">Waiting for product verification...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShubhamProductDetailsView;
